import { Box, Typography } from '@mui/material';
import { ReactComponent as BriefcaseIcon } from 'assets/briefcase.svg';
import { ReactComponent as CheckListIcon } from 'assets/check-list.svg';
import { ReactNode, useEffect, useState } from 'react';
import { TaskService } from 'service/task.service';
import zeplinColor from 'theme/Color';
import { Utilities } from 'utilities/utilities';
import { TaskFilters } from '../TasksFilters/TasksFilters';

type TaskType = 'total' | 'completed' | 'pending' | 'overdue';

type Statistic = {
  type: TaskType;
  taskNumber: number;
};

type StatisticDetailType = Record<
  TaskType,
  { backgroundColor: string; iconColor: string; icon: ReactNode }
>;

const StatisticsDetails: StatisticDetailType = {
  total: {
    backgroundColor: zeplinColor.Lavender,
    iconColor: zeplinColor.Blue4,
    icon: <BriefcaseIcon />,
  },
  completed: {
    backgroundColor: zeplinColor.Green5,
    iconColor: zeplinColor.Green3,
    icon: <CheckListIcon />,
  },
  pending: {
    backgroundColor: zeplinColor.Blue6,
    iconColor: zeplinColor.Blue,
    icon: <BriefcaseIcon />,
  },
  overdue: {
    backgroundColor: zeplinColor.BlockBG,
    iconColor: zeplinColor.Error,
    icon: <CheckListIcon />,
  },
};

type TasksStatisticsProps = {
  mine?: boolean;
  filters?: TaskFilters;
  statisticsRevision?: number;
};

export const TasksStatistics = ({
  mine = false,
  filters,
  statisticsRevision = 0,
}: TasksStatisticsProps) => {
  const [statisticsData, setStatisticsData] = useState<Statistic[]>([
    { type: 'total', taskNumber: 0 },
    { type: 'completed', taskNumber: 0 },
    { type: 'pending', taskNumber: 0 },
    { type: 'overdue', taskNumber: 0 },
  ]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStatistics = async () => {
      try {
        const res = await TaskService.getRollupNumbers({ mine });
        setStatisticsData([
          { type: 'total', taskNumber: res['Total Tasks'] || 0 },
          { type: 'completed', taskNumber: res['Completed Tasks'] || 0 },
          { type: 'pending', taskNumber: res['Pending Tasks'] || 0 },
          { type: 'overdue', taskNumber: res['OverDue Tasks'] || 0 },
        ]);
      } catch (err) {
        console.error('Failed to fetch task statistics:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchStatistics();
  }, [mine, filters, statisticsRevision]);

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: '8px',
        gap: '24px',
      }}
    >
      {statisticsData.map((item) => (
        <Box
          key={item.type}
          sx={{
            backgroundColor: StatisticsDetails[item.type].backgroundColor,
            width: '100%',
            borderRadius: '16px',
            padding: '20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
            }}
          >
            <Typography variant='responsiveDisplay5'>
              {loading ? '...' : item.taskNumber}
            </Typography>
            <Typography variant='responsiveBody3'>
              {Utilities.toCamelCase(item.type)} Tasks
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '48px',
              height: '48px',
              backgroundColor: StatisticsDetails[item.type].iconColor,
              borderRadius: '60px',
            }}
          >
            {StatisticsDetails[item.type].icon}
          </Box>
        </Box>
      ))}
    </Box>
  );
};
